import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Ensure all axios calls from this component send cookies — admin auth now relies on an httpOnly cookie.
axios.defaults.withCredentials = true;

import { Logo } from './Logo';
import { AdminLogin } from './AdminLogin';
import { AdminIntakeDetail } from './AdminIntakeDetail';
import { AdminUserManagement } from './AdminUserManagement';
import { AdminSurveyorManagement } from './AdminSurveyorManagement';
import { RecoverySettingsPanel } from './RecoverySettingsPanel';
import { AdminFunnelDashboard } from './AdminFunnelDashboard';
import { RateLibraryAdmin } from './admin/RateLibraryAdmin';
import { FoundingMembersAdmin } from './admin/FoundingMembersAdmin';
import { TestersAdmin } from './admin/TestersAdmin';
import { SiteModeAdmin } from './admin/SiteModeAdmin';
import { 
  LogOut, 
  Search, 
  Calendar,
  Clock,
  FileText,
  RefreshCw,
  CheckSquare,
  Square,
  MinusSquare,
  AlertTriangle,
  CheckCircle,
  Download,
  TrendingUp,
  PoundSterling,
  ShoppingCart,
  Percent
} from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: { bg: '#FEF3C7', text: '#92400E', label: 'Pending' },
  in_progress: { bg: '#DBEAFE', text: '#1E40AF', label: 'In Progress' },
  completed: { bg: '#DCFCE7', text: '#166534', label: 'Completed' },
  paid: { bg: '#FEF3C7', text: '#92400E', label: 'Pending' }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP' 
  }).format(amount || 0);
};

// Calculate if delivery is on track or overdue
const getDeliveryStatus = (deliveryDateStr, status) => {
  if (status === 'completed') return { status: 'completed', label: 'Completed', color: '#166534', bg: '#DCFCE7' };
  if (!deliveryDateStr) return { status: 'unknown', label: 'N/A', color: '#6B7280', bg: '#F3F4F6' };
  
  try {
    const deliveryDate = new Date(deliveryDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deliveryDate.setHours(0, 0, 0, 0);
    
    const daysUntilDue = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) {
      return { status: 'overdue', label: 'Overdue', color: '#DC2626', bg: '#FEE2E2' };
    } else if (daysUntilDue <= 2) {
      return { status: 'at_risk', label: 'At Risk', color: '#D97706', bg: '#FEF3C7' };
    } else {
      return { status: 'on_track', label: 'On Track', color: '#059669', bg: '#ECFDF5' };
    }
  } catch {
    return { status: 'unknown', label: 'N/A', color: '#6B7280', bg: '#F3F4F6' };
  }
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  // token kept in memory only — real auth is the httpOnly `admin_token` cookie (XSS-safe).
  // Role/name are cached in sessionStorage because they're non-sensitive and speed up re-renders.
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(sessionStorage.getItem('admin_role') || null);
  const [userName, setUserName] = useState(sessionStorage.getItem('admin_name') || '');
  const [intakes, setIntakes] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntake, setSelectedIntake] = useState(null);
  
  // Revenue stats state
  const [stats, setStats] = useState({
    total_orders: 0,
    total_revenue: 0,
    pipeline_value: 0,
    discounted_orders: 0,
    total_discounted_value: 0
  });
  const [statsPeriod, setStatsPeriod] = useState('all');
  
  // Period options for stats filter
  const periodOptions = [
    { value: 'all', label: 'All Time' },
    { value: '12m', label: '12 Months' },
    { value: '6m', label: '6 Months' },
    { value: '3m', label: '3 Months' },
    { value: '1m', label: '1 Month' },
    { value: '3w', label: '3 Weeks' },
    { value: '2w', label: '2 Weeks' },
    { value: '1w', label: '1 Week' },
  ];
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Bulk action state
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSurveyor, setBulkSurveyor] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('managed');
  const [scopeOrders, setScopeOrders] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  // Admin "Projects" tab — Feb 2026: lists ALL drafts (paid + unpaid) so the
  // admin team can grab any user's documents for support / pre-call review.
  const [allProjects, setAllProjects] = useState([]);
  const [allProjectsLoading, setAllProjectsLoading] = useState(false);
  const [projectsSearch, setProjectsSearch] = useState('');
  const [projectsPaidFilter, setProjectsPaidFilter] = useState('all'); // 'all' | 'paid' | 'unpaid'
  
  // QS Check delivery modal
  const [deliverModal, setDeliverModal] = useState(null);
  const [deliverNotes, setDeliverNotes] = useState('');
  const [deliverFile, setDeliverFile] = useState(null);
  const [delivering, setDelivering] = useState(false);

  // Pricing settings (QS Check launch offer)
  const [pricingSettings, setPricingSettings] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    launch_active: true,
    launch_price: 199,
    standard_price: 249,
    launch_end_date: '',
  });

  const isAdmin = userRole === 'admin';

  // On mount: the httpOnly cookie carries auth. Always call /admin/verify.
  // If the cookie is present + valid, we're in; otherwise we'll render the login screen.
  useEffect(() => {
    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifySession = async () => {
    try {
      // Cookie (withCredentials) carries auth. No Bearer header needed on the initial check.
      const response = await axios.get(`${API_URL}/api/admin/verify`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.data.valid) {
        setUserRole(response.data.role);
        setUserName(response.data.name);
        sessionStorage.setItem('admin_role', response.data.role);
        sessionStorage.setItem('admin_name', response.data.name);
        // Set a sentinel token so downstream components know we're authed.
        // Actual auth goes through the cookie — this value isn't used by the server directly.
        if (!token) setToken('cookie_authed');
        const authForFetches = token || 'cookie_authed';
        fetchIntakesWithToken(authForFetches);
        fetchSurveyors(authForFetches);
        fetchStats(authForFetches);
        fetchScopeOrders(authForFetches);
      } else {
        handleLogout();
      }
    } catch {
      setLoading(false);
    }
  };

  const fetchStats = async (authToken, period = statsPeriod) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats?period=${period}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Handle period change for stats
  const handlePeriodChange = async (newPeriod) => {
    setStatsPeriod(newPeriod);
    const currentToken = token || 'cookie_authed';
    if (currentToken) {
      await fetchStats(currentToken, newPeriod);
    }
  };

  const fetchSurveyors = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/surveyors`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSurveyors(response.data.surveyors || []);
    } catch (err) {
      console.error('Failed to fetch surveyors:', err);
    }
  };

  const fetchIntakesWithToken = async (authToken) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await axios.get(`${API_URL}/api/admin/intakes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setIntakes(response.data.intakes || []);
    } catch (err) {
      toast.error('Failed to fetch submissions');
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchScopeOrders = async (authToken) => {
    setScopeLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/scope-orders`, {
        headers: { 'X-Session-Token': authToken }
      });
      setScopeOrders(response.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch scope orders:', err);
    } finally {
      setScopeLoading(false);
    }
  };

  // Admin Projects tab — fetches ALL scope drafts (paid + unpaid) so the team
  // can grab any user's documents for support / QA / pre-call review.
  const fetchAllProjects = async (authToken, opts = {}) => {
    setAllProjectsLoading(true);
    try {
      const paidOnly = opts.paidOnly ? '?paid_only=true' : '';
      const response = await axios.get(`${API_URL}/api/admin/scope/projects${paidOnly}`, {
        headers: { 'X-Session-Token': authToken },
      });
      setAllProjects(response.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch admin projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setAllProjectsLoading(false);
    }
  };

  const handleDeliverQSCheck = async () => {
    if (!deliverModal || !deliverFile) return;
    setDelivering(true);
    try {
      const formData = new FormData();
      formData.append('order_ref', deliverModal.order_ref);
      formData.append('surveyor_notes', deliverNotes);
      formData.append('report', deliverFile);
      
      await axios.post(`${API_URL}/api/admin/deliver-qs-check`, formData, {
        headers: { 'X-Session-Token': token, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`QS Check report delivered for ${deliverModal.order_ref}`);
      setDeliverModal(null);
      setDeliverNotes('');
      setDeliverFile(null);
      fetchScopeOrders(token);
    } catch (err) {
      toast.error('Failed to deliver report');
    } finally {
      setDelivering(false);
    }
  };

  const fetchPricingSettings = async (authToken) => {
    setPricingLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings/qs-check-pricing`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPricingSettings(res.data);
      // Convert ISO end_date to local datetime-local input format
      const endDateInput = res.data.launch_end_date
        ? new Date(res.data.launch_end_date).toISOString().slice(0, 16)
        : '';
      setPricingForm({
        launch_active: !!res.data.launch_active,
        launch_price: res.data.launch_price,
        standard_price: res.data.standard_price,
        launch_end_date: endDateInput,
      });
    } catch (err) {
      console.error('Failed to fetch pricing settings:', err);
      toast.error('Failed to load pricing settings');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleSavePricing = async () => {
    setPricingSaving(true);
    try {
      const payload = {
        launch_active: !!pricingForm.launch_active,
        launch_price: Number(pricingForm.launch_price),
        standard_price: Number(pricingForm.standard_price),
      };
      if (pricingForm.launch_end_date) {
        payload.launch_end_date = new Date(pricingForm.launch_end_date).toISOString();
      } else {
        payload.clear_end_date = true;
      }
      const res = await axios.put(`${API_URL}/api/admin/settings/qs-check-pricing`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPricingSettings(res.data);
      toast.success('Pricing saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setPricingSaving(false);
    }
  };

  const fetchIntakes = useCallback(async () => {
    const currentToken = token || 'cookie_authed';
    if (currentToken) {
      await fetchIntakesWithToken(currentToken);
      await fetchStats(currentToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, dateFrom, dateTo]);

  const handleLogout = async () => {
    try {
      // Clear the httpOnly admin_token cookie server-side
      await axios.post(`${API_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Best-effort: continue clearing local state even if the call fails
    }
    sessionStorage.removeItem('admin_role');
    sessionStorage.removeItem('admin_name');
    setToken(null);
    setUserRole(null);
    setUserName('');
    toast.info('Logged out');
  };

  const handleLogin = (newToken, role, name) => {
    setToken(newToken);
    setUserRole(role);
    setUserName(name);
    sessionStorage.setItem('admin_role', role);
    sessionStorage.setItem('admin_name', name);
    fetchIntakesWithToken(newToken);
    fetchSurveyors(newToken);
  };

  const handleIntakeClick = (intake, e) => {
    // Don't navigate if clicking on a dropdown or checkbox
    if (e.target.closest('[data-no-navigate]') || e.target.closest('button')) {
      return;
    }
    setSelectedIntake(intake.intakeId);
  };

  const handleBackToList = () => {
    setSelectedIntake(null);
    setSelectedIds(new Set());
    fetchIntakes();
  };

  // Inline update handlers
  const handleInlineStatusChange = async (intakeId, newStatus) => {
    const currentToken = token || 'cookie_authed';
    try {
      await axios.put(
        `${API_URL}/api/admin/intakes/${intakeId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      // Update local state
      setIntakes(prev => prev.map(i => 
        i.intakeId === intakeId ? { ...i, status: newStatus } : i
      ));
      toast.success(`Status updated to ${statusColors[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleInlineSurveyorChange = async (intakeId, surveyorId) => {
    const currentToken = token || 'cookie_authed';
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/intakes/${intakeId}/surveyor`,
        { surveyor_id: surveyorId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      // Update local state
      const surveyor = response.data.surveyor;
      setIntakes(prev => prev.map(i => 
        i.intakeId === intakeId 
          ? { ...i, assigned_surveyor: surveyor.id, assigned_surveyor_name: surveyor.name } 
          : i
      ));
      toast.success(`Assigned to ${surveyor.name}`);
    } catch (err) {
      toast.error('Failed to assign surveyor');
    }
  };

  // Selection handlers
  const toggleSelection = (intakeId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(intakeId)) {
        newSet.delete(intakeId);
      } else {
        newSet.add(intakeId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredIntakes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIntakes.map(i => i.intakeId)));
    }
  };

  // Bulk action handler
  const handleBulkAction = async () => {
    if (selectedIds.size === 0) {
      toast.error('No submissions selected');
      return;
    }
    if (!bulkStatus && !bulkSurveyor) {
      toast.error('Select a status or surveyor to apply');
      return;
    }

    setBulkLoading(true);
    const currentToken = token || 'cookie_authed';

    try {
      const response = await axios.put(
        `${API_URL}/api/admin/intakes/bulk-update`,
        {
          intake_ids: Array.from(selectedIds),
          status: bulkStatus || null,
          surveyor_id: bulkSurveyor || null
        },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );

      toast.success(response.data.message);
      
      // Refresh the list
      await fetchIntakes();
      
      // Clear selections
      setSelectedIds(new Set());
      setBulkStatus('');
      setBulkSurveyor('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // Filter intakes by search term
  const filteredIntakes = intakes.filter(intake => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      intake.intake_reference_id?.toLowerCase().includes(search) ||
      intake.client_name?.toLowerCase().includes(search) ||
      intake.client_email?.toLowerCase().includes(search)
    );
  });

  // CSV Export handler
  const exportToCSV = () => {
    // CSV Header row
    const headers = [
      'REFERENCE',
      'CLIENT NAME',
      'CLIENT EMAIL',
      'DATE SUBMITTED',
      'AMOUNT PAID (£)',
      'SURVEYOR ASSIGNED',
      'STATUS',
      'DUE DATE',
      'ON TRACK / OVERDUE'
    ];

    // Build rows from filtered intakes
    const rows = filteredIntakes.map(intake => {
      const deliveryStatus = getDeliveryStatus(intake.estimated_delivery_date, intake.status);
      const statusLabel = statusColors[intake.status]?.label || intake.status || 'Unknown';
      
      return [
        intake.intake_reference_id || '',
        intake.client_name || '',
        intake.client_email || '',
        formatDate(intake.date_submitted),
        intake.quote_amount?.toFixed(2) || '0.00',
        intake.assigned_surveyor_name || 'Unassigned',
        statusLabel,
        formatDate(intake.estimated_delivery_date),
        deliveryStatus.label
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape cells containing commas or quotes
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Create filename with today's date
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    const filename = `SOW-Export-${dateStr}.csv`;

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);

    toast.success(`Exported ${filteredIntakes.length} submissions to ${filename}`);
  };

  // Checkbox state
  const allSelected = filteredIntakes.length > 0 && selectedIds.size === filteredIntakes.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filteredIntakes.length;

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (selectedIntake) {
    return (
      <AdminIntakeDetail 
        intakeId={selectedIntake} 
        token={token}
        userRole={userRole}
        onBack={handleBackToList}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="navbar">
        <div className="navbar-inner">
          <div className="flex items-center gap-4">
            <Logo variant="dark" />
            <span className="text-sm font-medium px-3 py-1 rounded-full" style={{ backgroundColor: isAdmin ? 'var(--color-navy)' : '#059669', color: 'white' }}>
              {isAdmin ? 'Admin' : 'Surveyor'}
            </span>
            {userName && (
              <span className="text-sm" style={{ color: 'var(--color-muted)' }}>
                {userName}
              </span>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="btn-secondary flex items-center gap-2"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Title with Export Button */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-navy)' }}>
                {{ pricing: 'Settings', founding: 'Founding members', testers: 'Testers', sitemode: 'Site mode' }[activeTab] || 'Submissions'}
              </h1>
              <p style={{ color: 'var(--color-muted)' }}>
                {{ pricing: 'Pricing and platform configuration', founding: 'Waitlist sign-ups and survey answers', testers: 'Tester accounts for founding mode', sitemode: 'Public access and email delivery' }[activeTab] || `${filteredIntakes.length} total submissions`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Tab Switcher */}
              <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border)' }} data-testid="admin-tabs">
                <button
                  onClick={() => setActiveTab('managed')}
                  className="px-4 py-2 text-sm font-medium"
                  style={{
                    backgroundColor: activeTab === 'managed' ? 'var(--color-navy)' : 'white',
                    color: activeTab === 'managed' ? 'white' : 'var(--color-muted)',
                  }}
                  data-testid="tab-managed"
                >
                  Managed Service
                </button>
                <button
                  onClick={() => { setActiveTab('scope'); fetchScopeOrders(token); }}
                  className="px-4 py-2 text-sm font-medium"
                  style={{
                    backgroundColor: activeTab === 'scope' ? 'var(--color-navy)' : 'white',
                    color: activeTab === 'scope' ? 'white' : 'var(--color-muted)',
                    borderLeft: '1px solid var(--color-border)',
                  }}
                  data-testid="tab-scope"
                >
                  Scope Orders {scopeOrders.length > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>({scopeOrders.length})</span>}
                </button>
                {/* All Projects (Feb 2026) — admin can browse + download every user's documents */}
                {isAdmin && (
                  <button
                    onClick={() => { setActiveTab('projects'); fetchAllProjects(token); }}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'projects' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'projects' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-projects"
                  >
                    Projects
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => { setActiveTab('pricing'); fetchPricingSettings(token); }}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'pricing' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'pricing' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-pricing"
                  >
                    Pricing
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('users')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'users' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'users' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-users"
                  >
                    Users
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('funnel')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'funnel' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'funnel' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-funnel"
                  >
                    Funnel
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('surveyors')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'surveyors' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'surveyors' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-surveyors"
                  >
                    Surveyors
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('rates')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'rates' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'rates' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-rates"
                  >
                    Rates
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('founding')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'founding' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'founding' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-founding"
                  >
                    Founding members
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('testers')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'testers' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'testers' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-testers"
                  >
                    Testers
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('sitemode')}
                    className="px-4 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: activeTab === 'sitemode' ? 'var(--color-navy)' : 'white',
                      color: activeTab === 'sitemode' ? 'white' : 'var(--color-muted)',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                    data-testid="tab-sitemode"
                  >
                    Site mode
                  </button>
                )}
              </div>
              {!['founding', 'testers', 'sitemode'].includes(activeTab) && (<>
              <button
                onClick={exportToCSV}
                className="btn-secondary flex items-center gap-2"
                disabled={filteredIntakes.length === 0}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={fetchIntakes}
                className="btn-secondary flex items-center gap-2"
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              </>)}
            </div>
          </div>

          {/* Lifetime Revenue Summary Bar */}
          {activeTab === 'managed' && (
          <>
          <div className="mb-6">
            {/* Header with Period Filter */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                Showing: {periodOptions.find(p => p.value === statsPeriod)?.label || 'All Time'}
              </p>
              {/* Period Filter - Segmented Pill Selector */}
              <div 
                className="flex items-center rounded-lg overflow-hidden"
                style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg)' }}
                data-testid="stats-period-filter"
              >
                {periodOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handlePeriodChange(option.value)}
                    className="px-3 py-1.5 text-xs font-medium transition-all"
                    style={{
                      backgroundColor: statsPeriod === option.value ? 'var(--color-navy)' : 'transparent',
                      color: statsPeriod === option.value ? 'white' : 'var(--color-muted)',
                    }}
                    data-testid={`period-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total Orders */}
              <div 
                className="card p-5 flex items-center gap-4"
                style={{ border: '1px solid var(--color-border)' }}
                data-testid="stat-total-orders"
              >
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg)' }}>
                  <ShoppingCart className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>Total Orders</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-navy)' }}>
                    {stats.total_orders.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Total Revenue (Completed) */}
              <div 
                className="card p-5 flex items-center gap-4"
                style={{ border: '1px solid var(--color-border)' }}
                data-testid="stat-total-revenue"
              >
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg)' }}>
                  <PoundSterling className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>Total Revenue</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-navy)' }}>
                    {formatCurrency(stats.total_revenue)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Completed orders</p>
                </div>
              </div>

              {/* Pipeline Value */}
              <div 
                className="card p-5 flex items-center gap-4"
                style={{ border: '1px solid var(--color-border)' }}
                data-testid="stat-pipeline-value"
              >
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg)' }}>
                  <TrendingUp className="w-6 h-6" style={{ color: 'var(--color-navy)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>Pipeline Value</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-navy)' }}>
                    {formatCurrency(stats.pipeline_value)}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Pending and In Progress</p>
                </div>
              </div>

              {/* Discount Impact */}
              <div 
                className="card p-5 flex items-center gap-4"
                style={{ border: '1px solid var(--color-border)' }}
                data-testid="stat-discounts"
              >
                <div className="p-3 rounded-lg" style={{ backgroundColor: stats.discounted_orders > 0 ? '#FEF3C7' : 'var(--color-bg)' }}>
                  <Percent className="w-6 h-6" style={{ color: stats.discounted_orders > 0 ? '#D97706' : 'var(--color-navy)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>Discounts Used</p>
                  <p className="text-2xl font-bold" style={{ color: stats.discounted_orders > 0 ? '#D97706' : 'var(--color-navy)' }}>
                    {stats.discounted_orders}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    {formatCurrency(stats.total_discounted_value)} value
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && isAdmin && (
            <div 
              className="card p-4 mb-6 flex flex-wrap items-center gap-4"
              style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}
              data-testid="bulk-action-bar"
            >
              <div className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                <span className="font-medium" style={{ color: 'var(--color-navy)' }}>
                  {selectedIds.size} selected
                </span>
              </div>
              
              <div className="flex-1" />
              
              <div className="flex flex-wrap items-center gap-3">
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className="w-40 bg-white" data-testid="bulk-status-select">
                    <SelectValue placeholder="Set Status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={bulkSurveyor} onValueChange={setBulkSurveyor}>
                  <SelectTrigger className="w-48 bg-white" data-testid="bulk-surveyor-select">
                    <SelectValue placeholder="Assign Surveyor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {surveyors.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <button
                  onClick={handleBulkAction}
                  disabled={bulkLoading || (!bulkStatus && !bulkSurveyor)}
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                  data-testid="bulk-apply-btn"
                >
                  {bulkLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                  Apply
                </button>

                <button
                  onClick={() => { setSelectedIds(new Set()); setBulkStatus(''); setBulkSurveyor(''); }}
                  className="btn-secondary px-4 py-2"
                  data-testid="bulk-clear-btn"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="input-label block mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-muted)' }} />
                  <Input
                    className="input-styled pl-10"
                    placeholder="Reference, name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="admin-search-input"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="w-48">
                <label className="input-label block mb-2">Status</label>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(fetchIntakes, 100); }}>
                  <SelectTrigger className="input-styled" data-testid="admin-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div className="w-40">
                <label className="input-label block mb-2">From Date</label>
                <Input
                  className="input-styled"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setTimeout(fetchIntakes, 100); }}
                  data-testid="admin-date-from"
                />
              </div>

              {/* Date To */}
              <div className="w-40">
                <label className="input-label block mb-2">To Date</label>
                <Input
                  className="input-styled"
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setTimeout(fetchIntakes, 100); }}
                  data-testid="admin-date-to"
                />
              </div>
            </div>
          </div>

          {/* Submissions Table */}
          <div className="card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: 'var(--color-navy)' }} />
                <p style={{ color: 'var(--color-muted)' }}>Loading submissions...</p>
              </div>
            ) : filteredIntakes.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-muted)' }} />
                <p className="font-medium" style={{ color: 'var(--color-text)' }}>No submissions found</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>
                  {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Submissions will appear here when clients complete orders'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-submissions-table">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg)' }}>
                      {/* Checkbox header */}
                      {isAdmin && (
                        <th className="px-3 py-3 text-left">
                          <button 
                            onClick={toggleSelectAll}
                            className="p-1 hover:bg-gray-200 rounded"
                            data-testid="select-all-checkbox"
                          >
                            {allSelected ? (
                              <CheckSquare className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                            ) : someSelected ? (
                              <MinusSquare className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                            ) : (
                              <Square className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />
                            )}
                          </button>
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Submitted</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Surveyor</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>Track</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                    {filteredIntakes.map((intake) => {
                      const status = statusColors[intake.status] || statusColors.pending;
                      const deliveryStatus = getDeliveryStatus(intake.estimated_delivery_date, intake.status);
                      const isSelected = selectedIds.has(intake.intakeId);
                      
                      return (
                        <tr
                          key={intake.intakeId}
                          onClick={(e) => handleIntakeClick(intake, e)}
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                          data-testid={`intake-row-${intake.intakeId}`}
                        >
                          {/* Checkbox */}
                          {isAdmin && (
                            <td className="px-3 py-4" data-no-navigate>
                              <button 
                                onClick={(e) => { e.stopPropagation(); toggleSelection(intake.intakeId); }}
                                className="p-1 hover:bg-gray-200 rounded"
                                data-testid={`checkbox-${intake.intakeId}`}
                              >
                                {isSelected ? (
                                  <CheckSquare className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                                ) : (
                                  <Square className="w-5 h-5" style={{ color: 'var(--color-muted)' }} />
                                )}
                              </button>
                            </td>
                          )}
                          <td className="px-4 py-4">
                            <span className="font-mono font-medium" style={{ color: 'var(--color-navy)' }}>
                              {intake.intake_reference_id}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium" style={{ color: 'var(--color-text)' }}>{intake.client_name}</p>
                              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>{intake.client_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span style={{ color: 'var(--color-text)' }}>{formatDate(intake.date_submitted)}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="font-medium" style={{ color: 'var(--color-text)' }}>
                              {formatCurrency(intake.quote_amount)}
                            </span>
                          </td>
                          {/* Inline Surveyor Dropdown */}
                          <td className="px-4 py-4" data-no-navigate>
                            {isAdmin ? (
                              <Select 
                                value={intake.assigned_surveyor || ''} 
                                onValueChange={(val) => handleInlineSurveyorChange(intake.intakeId, val)}
                              >
                                <SelectTrigger 
                                  className="w-36 h-8 text-sm"
                                  style={{ 
                                    backgroundColor: intake.assigned_surveyor ? 'white' : '#FEF3C7',
                                    borderColor: intake.assigned_surveyor ? 'var(--color-border)' : '#F59E0B'
                                  }}
                                  data-testid={`surveyor-select-${intake.intakeId}`}
                                >
                                  <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent>
                                  {surveyors.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span style={{ color: intake.assigned_surveyor ? 'var(--color-text)' : 'var(--color-muted)' }}>
                                {intake.assigned_surveyor_name || 'Unassigned'}
                              </span>
                            )}
                          </td>
                          {/* Inline Status Dropdown */}
                          <td className="px-4 py-4" data-no-navigate>
                            {isAdmin ? (
                              <Select 
                                value={intake.status || 'pending'} 
                                onValueChange={(val) => handleInlineStatusChange(intake.intakeId, val)}
                              >
                                <SelectTrigger 
                                  className="w-32 h-8 text-sm"
                                  style={{ backgroundColor: status.bg, borderColor: status.bg }}
                                  data-testid={`status-select-${intake.intakeId}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span 
                                className="px-2 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: status.bg, color: status.text }}
                              >
                                {status.label}
                              </span>
                            )}
                          </td>
                          {/* Due Date */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" style={{ color: 'var(--color-muted)' }} />
                              <span style={{ color: 'var(--color-text)' }}>
                                {formatDate(intake.estimated_delivery_date)}
                              </span>
                            </div>
                          </td>
                          {/* On Track / Overdue */}
                          <td className="px-4 py-4">
                            <span 
                              className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit"
                              style={{ backgroundColor: deliveryStatus.bg, color: deliveryStatus.color }}
                            >
                              {deliveryStatus.status === 'overdue' && <AlertTriangle className="w-3 h-3" />}
                              {deliveryStatus.status === 'on_track' && <CheckCircle className="w-3 h-3" />}
                              {deliveryStatus.status === 'at_risk' && <Clock className="w-3 h-3" />}
                              {deliveryStatus.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
          )}

          {/* SCOPE ORDERS TAB */}
          {activeTab === 'scope' && (
            <div className="card" style={{ overflow: 'hidden' }} data-testid="scope-orders-table">
              {scopeLoading ? (
                <div className="p-8 text-center" style={{ color: 'var(--color-muted)' }}>Loading scope orders...</div>
              ) : scopeOrders.length === 0 ? (
                <div className="p-8 text-center" style={{ color: 'var(--color-muted)' }}>No scope orders yet</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-border)', backgroundColor: 'var(--color-bg)' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Reference</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Product</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Client Email</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Address</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Range</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Region</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Status</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Date</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--color-navy)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scopeOrders.map((order, i) => {
                        const productLabels = { indicative: 'Indicative (£29)', detailed: 'Full Project Pack (£199)', qs_check: 'QS Check' };
                        const statusStyle = order.status === 'paid'
                          ? { bg: '#DCFCE7', text: '#166534', label: 'Paid' }
                          : order.status === 'pending_payment'
                          ? { bg: '#FEF3C7', text: '#92400E', label: 'Pending Payment' }
                          : { bg: '#F3F4F6', text: '#6B7280', label: order.status || 'Calculated' };
                        return (
                          <tr key={order.project_id || i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--color-navy)' }}>{order.order_ref || '—'}</td>
                            <td style={{ padding: '10px 12px' }}>{productLabels[order.product_type] || order.product_type}</td>
                            <td style={{ padding: '10px 12px' }}>{order.client_email || '—'}</td>
                            <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.address || order.address_full || '—'}</td>
                            <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                              {order.output_range_low && order.output_range_high
                                ? `£${Math.round(order.output_range_low / 1000)}k – £${Math.round(order.output_range_high / 1000)}k`
                                : '—'}
                            </td>
                            <td style={{ padding: '10px 12px' }}>{order.region || '—'}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                                {statusStyle.label}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', color: 'var(--color-muted)' }}>{formatDate(order.created_at)}</td>
                            <td style={{ padding: '10px 12px' }}>
                              {order.product_type === 'qs_check' && order.status === 'paid' && !order.qs_delivered && (
                                <button
                                  onClick={() => setDeliverModal(order)}
                                  style={{ padding: '4px 10px', background: 'var(--color-navy)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                  data-testid={`deliver-${order.order_ref}`}
                                >
                                  Deliver Report
                                </button>
                              )}
                              {order.qs_delivered && (
                                <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 600 }}>Delivered</span>
                              )}
                              {order.draft_id && (
                                <a
                                  href={`${API_URL}/api/admin/scope/drafts/${order.draft_id}/qs-internal.xlsx`}
                                  download
                                  style={{ marginLeft: 8, padding: '4px 10px', background: '#1E40AF', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}
                                  data-testid={`qs-internal-xlsx-${order.order_ref}`}
                                  title="Download QS Internal Report (Excel) — for QS pre-call review"
                                >
                                  QS Excel
                                </a>
                              )}
                              {/* Admin-only customer document downloads (Feb 2026) */}
                              {order.draft_id && (
                                <span data-testid={`admin-docs-${order.order_ref}`} style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
                                  <a href={`${API_URL}/api/admin/scope/drafts/${order.draft_id}/sow.pdf`} download
                                     style={{ padding: '4px 8px', background: '#fff', border: '1px solid var(--color-border)', color: 'var(--color-navy)', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                                     title="Customer SoW (PDF)">SoW</a>
                                  <a href={`${API_URL}/api/admin/scope/drafts/${order.draft_id}/sow.xlsx`} download
                                     style={{ padding: '4px 8px', background: '#fff', border: '1px solid var(--color-border)', color: 'var(--color-navy)', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                                     title="Customer SoW (Excel)">SoW XLS</a>
                                  <a href={`${API_URL}/api/admin/scope/drafts/${order.draft_id}/client-estimate.pdf`} download
                                     style={{ padding: '4px 8px', background: '#fff', border: '1px solid var(--color-border)', color: 'var(--color-navy)', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                                     title="Customer Cost Estimate (PDF)">Estimate</a>
                                  <a href={`${API_URL}/api/admin/scope/drafts/${order.draft_id}/materials.pdf`} download
                                     style={{ padding: '4px 8px', background: '#fff', border: '1px solid var(--color-border)', color: 'var(--color-navy)', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                                     title="Customer Materials (PDF)">Materials</a>
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ALL PROJECTS TAB — Feb 2026: admin browser for every draft */}
          {activeTab === 'projects' && isAdmin && (
            <div className="card" style={{ overflow: 'hidden' }} data-testid="all-projects-tab">
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 2 }}>All Projects</h2>
                  <p style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                    Every draft in the system (paid + unpaid). Use this to grab a customer's documents on a support call or pre-QS-call review. {allProjects.length} project{allProjects.length === 1 ? '' : 's'} loaded.
                  </p>
                </div>
                <input
                  type="text"
                  placeholder="Search by email / address / postcode / order ref…"
                  value={projectsSearch}
                  onChange={(e) => setProjectsSearch(e.target.value)}
                  data-testid="projects-search"
                  style={{ flex: 1, minWidth: 260, padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }}
                />
                <select
                  value={projectsPaidFilter}
                  onChange={(e) => setProjectsPaidFilter(e.target.value)}
                  data-testid="projects-paid-filter"
                  style={{ padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, background: '#fff' }}
                >
                  <option value="all">All</option>
                  <option value="paid">Paid only</option>
                  <option value="unpaid">Unpaid only</option>
                </select>
                <button
                  onClick={() => fetchAllProjects(token)}
                  className="btn-secondary"
                  data-testid="projects-refresh"
                  disabled={allProjectsLoading}
                  style={{ padding: '8px 14px', fontSize: 12 }}
                >
                  <RefreshCw className={`w-3 h-3 ${allProjectsLoading ? 'animate-spin' : ''}`} style={{ display: 'inline-block', marginRight: 4 }} />
                  Refresh
                </button>
              </div>
              {allProjectsLoading ? (
                <div className="p-8 text-center" style={{ color: 'var(--color-muted)' }}>Loading projects…</div>
              ) : allProjects.length === 0 ? (
                <div className="p-8 text-center" style={{ color: 'var(--color-muted)' }}>No projects yet</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-border)', backgroundColor: 'var(--color-bg)' }}>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Owner</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Address</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Order Ref</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Paid</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Total</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Updated</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Customer Documents</th>
                        <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-navy)' }}>Internal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allProjects
                        .filter(p => {
                          if (projectsPaidFilter === 'paid' && !p.documents_paid) return false;
                          if (projectsPaidFilter === 'unpaid' && p.documents_paid) return false;
                          if (!projectsSearch) return true;
                          const q = projectsSearch.trim().toLowerCase();
                          return [p.owner_email, p.address, p.postcode, p.order_ref, p.draft_id]
                            .some(v => (v || '').toString().toLowerCase().includes(q));
                        })
                        .map((p) => {
                          const total = p.last_project_total
                            ? `£${Math.round(p.last_project_total).toLocaleString('en-GB')}`
                            : '—';
                          const docBtn = (path, label, title) => (
                            <a
                              href={`${API_URL}/api/admin/scope/drafts/${p.draft_id}/${path}`}
                              download
                              data-testid={`admin-doc-${path.replace(/\./g, '-')}-${p.draft_id}`}
                              title={title}
                              style={{ padding: '3px 8px', background: '#fff', border: '1px solid var(--color-border)', color: 'var(--color-navy)', borderRadius: 5, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
                            >
                              {label}
                            </a>
                          );
                          return (
                            <tr key={p.draft_id} data-testid={`admin-project-row-${p.draft_id}`}
                              style={{ borderBottom: '1px solid var(--color-border)' }}>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                                {p.owner_email || <span style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>Anonymous</span>}
                              </td>
                              <td style={{ padding: '8px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={p.address}>
                                {p.address || <span style={{ color: 'var(--color-muted)' }}>—</span>}
                                {p.postcode && <div style={{ fontSize: 10.5, color: 'var(--color-muted)' }}>{p.postcode}</div>}
                              </td>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                                {p.order_ref || `DRAFT-${(p.draft_id || '').slice(0, 8).toUpperCase()}`}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {p.documents_paid ? (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: '#DCFCE7', color: '#166534' }}>Paid</span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, backgroundColor: '#F3F4F6', color: '#6B7280' }}>Unpaid</span>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>{total}</td>
                              <td style={{ padding: '8px 12px', color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>
                                {formatDate(p.updated_at)}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {docBtn('sow.pdf', 'SoW', 'Schedule of Works (PDF)')}
                                  {docBtn('sow.xlsx', 'SoW XLS', 'Schedule of Works (Excel)')}
                                  {docBtn('client-estimate.pdf', 'Estimate', 'Cost Estimate (PDF)')}
                                  {docBtn('materials.pdf', 'Materials', 'Materials Schedule (PDF)')}
                                </div>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <a
                                  href={`${API_URL}/api/admin/scope/drafts/${p.draft_id}/qs-internal.xlsx`}
                                  download
                                  data-testid={`admin-qs-internal-${p.draft_id}`}
                                  title="QS Internal Report (Excel)"
                                  style={{ padding: '3px 8px', background: '#1E40AF', color: '#fff', borderRadius: 5, fontSize: 11, fontWeight: 700, textDecoration: 'none' }}
                                >
                                  QS Excel
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* PRICING TAB */}
          {activeTab === 'pricing' && (
            <div className="card" style={{ padding: 24 }} data-testid="pricing-tab">
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 4 }}>QS Check pricing</h2>
                <p style={{ fontSize: 13, color: 'var(--color-muted)' }}>
                  Control the QS Check price shown across the site, emails and Stripe checkout. Changes take effect immediately — no deployment needed.
                </p>
              </div>

              {pricingLoading && <p style={{ fontSize: 13, color: 'var(--color-muted)' }}>Loading…</p>}

              {!pricingLoading && pricingSettings && (
                <>
                  {/* Live preview strip */}
                  <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, marginBottom: 20 }} data-testid="pricing-preview">
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--color-muted)', marginBottom: 8 }}>Currently live</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                      {pricingSettings.effective_launch_active ? (
                        <>
                          <span style={{ fontSize: 20, fontWeight: 500, color: '#94A3B8', textDecoration: 'line-through' }}>£{pricingSettings.standard_price}</span>
                          <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--color-navy)', lineHeight: 1 }}>£{pricingSettings.launch_price}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 999 }}>Launch offer</span>
                        </>
                      ) : (
                        <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--color-navy)', lineHeight: 1 }}>£{pricingSettings.standard_price}</span>
                      )}
                    </div>
                    {pricingSettings.launch_end_date && (
                      <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 6 }}>
                        Auto-switch to standard price on {new Date(pricingSettings.launch_end_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>

                  {/* Form */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-navy)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        Launch price (£)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={pricingForm.launch_price}
                        onChange={(e) => setPricingForm(f => ({ ...f, launch_price: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 14 }}
                        data-testid="launch-price-input"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-navy)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        Standard price (£)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={pricingForm.standard_price}
                        onChange={(e) => setPricingForm(f => ({ ...f, standard_price: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 14 }}
                        data-testid="standard-price-input"
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-navy)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!pricingForm.launch_active}
                        onChange={(e) => setPricingForm(f => ({ ...f, launch_active: e.target.checked }))}
                        data-testid="launch-active-toggle"
                      />
                      Launch offer active (show crossed-out price + "Launch offer" badge)
                    </label>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-navy)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      Launch offer ends (optional — auto-switches to standard price)
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="datetime-local"
                        value={pricingForm.launch_end_date}
                        onChange={(e) => setPricingForm(f => ({ ...f, launch_end_date: e.target.value }))}
                        style={{ padding: '10px 12px', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 14 }}
                        data-testid="launch-end-date-input"
                      />
                      {pricingForm.launch_end_date && (
                        <button
                          onClick={() => setPricingForm(f => ({ ...f, launch_end_date: '' }))}
                          style={{ fontSize: 12, color: 'var(--color-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 6 }}>
                      Leave blank to keep the launch offer running indefinitely. When set, the price switches automatically at this time — no admin action needed.
                    </p>
                  </div>

                  <button
                    onClick={handleSavePricing}
                    disabled={pricingSaving}
                    style={{ padding: '10px 22px', background: 'var(--color-navy)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: pricingSaving ? 'default' : 'pointer', opacity: pricingSaving ? 0.6 : 1 }}
                    data-testid="save-pricing-btn"
                  >
                    {pricingSaving ? 'Saving…' : 'Save pricing'}
                  </button>
                </>
              )}

              {/* ── Recovery email (D+3 discount) ──────────────────── */}
              <RecoverySettingsPanel token={token} />
            </div>
          )}

          {/* QS CHECK DELIVERY MODAL */}
          {deliverModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="deliver-modal">
              <div style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 4 }}>Deliver QS Check Report</h3>
                <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 16 }}>
                  {deliverModal.order_ref} · {deliverModal.client_email}
                </p>
                
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-navy)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Upload QS-verified report (PDF) *
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setDeliverFile(e.target.files?.[0] || null)}
                    style={{ fontSize: 13 }}
                    data-testid="deliver-file-input"
                  />
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-navy)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Surveyor notes (optional)
                  </label>
                  <textarea
                    value={deliverNotes}
                    onChange={(e) => setDeliverNotes(e.target.value)}
                    placeholder="Any notes for the client about the review..."
                    rows={3}
                    style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                    data-testid="deliver-notes-input"
                  />
                </div>
                
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setDeliverModal(null); setDeliverNotes(''); setDeliverFile(null); }}
                    style={{ padding: '8px 16px', background: '#fff', border: '1.5px solid var(--color-border)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeliverQSCheck}
                    disabled={!deliverFile || delivering}
                    style={{ padding: '8px 16px', background: 'var(--color-navy)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: !deliverFile || delivering ? 0.5 : 1 }}
                    data-testid="deliver-submit-btn"
                  >
                    {delivering ? 'Delivering...' : 'Deliver Report and Send Email'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && isAdmin && (
            <AdminUserManagement token={token} />
          )}

          {activeTab === 'surveyors' && isAdmin && (
            <AdminSurveyorManagement token={token} />
          )}

          {activeTab === 'funnel' && isAdmin && (
            <AdminFunnelDashboard token={token} />
          )}

          {activeTab === 'rates' && isAdmin && (
            <RateLibraryAdmin token={token} />
          )}

          {activeTab === 'founding' && isAdmin && <FoundingMembersAdmin token={token} />}
          {activeTab === 'testers' && isAdmin && <TestersAdmin token={token} />}
          {activeTab === 'sitemode' && isAdmin && <SiteModeAdmin token={token} />}

        </div>
      </main>
    </div>
  );
};
